import { useState, useEffect, useRef } from "react";
import RecipientsToolbar from "./RecipientsToolbar";
import RecipientsTable from "./RecipientsTable";
import Pagination from "../components/Pagination";
import api from "../../../src/api_interceptor/api";
import Loader from "../../components/loader/Loader";
import GenericModal from "../components/GenericModal";
import { useNavigate } from "react-router-dom";

const Recipients = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState([]);
  const hasFetched = useRef(false);
  const [searchText, setSearchText] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingId, setPendingId] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageLength] = useState(10);
  const navigate = useNavigate();

  const [modalState, setModalState] = useState({
    open: false,
    title: "",
    description: "",
    type: "success",
  });

  const handleToggleStatus = (id, currentStatusCode) => {
    setPendingId(id);
    const isCurrentlyActive =
      currentStatusCode === 1 ||
      currentStatusCode === true ||
      currentStatusCode === "1";
    const newStatusCode = !isCurrentlyActive;
    setPendingStatus(newStatusCode);
    setModalOpen(true);
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    getList();
  }, []);

  useEffect(() => {
    const delay = setTimeout(() => {
      setCurrentPage(1);
      getList();
    }, 400);

    return () => clearTimeout(delay);
  }, [searchText]);

  useEffect(() => {
    if (hasFetched.current) {
      getList();
    }
  }, [currentPage]);

  const showSuccess = (message) => {
    setModalState({
      open: true,
      title: "Success",
      description: message,
      type: "success",
    });
  };

  const showError = (message) => {
    setModalState({
      open: true,
      title: "Error",
      description: message || "Something went wrong. Please try again.",
      type: "error",
    });
  };

  const showWarning = (message) => {
    setModalState({
      open: true,
      title: "Action Not Allowed",
      description: message,
      type: "warning",
    });
  };

  const getList = () => {
    setIsLoading(true);
    const API_URL = "api/GetAllRecipients";
    const req_body = {
      filter: searchText || "",
      pageIndex: currentPage - 1,
      totalPage: totalPages,
      pageLength: pageLength,
      errorMessage: "",
      Type: "Recipient",
      SortAction: "None",
    };

    api
      .post(API_URL, req_body, {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      })
      .then((response) => {
        setIsLoading(false);
        if (response && response.data) {
          if (
            response.data.AllList !== undefined &&
            response.data.AllList !== null
          ) {
            setItems(response.data.AllList);
            setTotalPages(response.data.totalPage || 1);
          } else if (
            response.data.errorMessage &&
            response.data.errorMessage.toLowerCase() !== "success"
          ) {
            const errorMsg =
              response.data.errorMessage === "Exception"
                ? "An error occurred while loading recipients. Please try again."
                : response.data.errorMessage || "Failed to load recipients.";
            showError(errorMsg);
          } else {
            setItems([]);
          }
        } else {
          showError("Invalid response from server. Please try again.");
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error("Get recipient list error:", error);

        if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
          showError(
            "Unable to connect to server. Please check your internet connection."
          );
        } else if (error.response) {
          const errorMsg =
            error.response.data?.Message ||
            "Failed to load recipients. Please try again.";
          showError(errorMsg);
        } else {
          showError("An unexpected error occurred. Please try again.");
        }
      });
  };

  const handleEdit = (row) => {
    if (row.StatusCodeId !== 1 && row.StatusCodeId !== true) {
      showWarning("Please activate the recipient before editing.");
      return;
    }
    navigate(`/recipient-master/edit/${row.ID}`);
  };

  const updateStatus = (id, isActivated) => {
    setIsLoading(true);
    const API_URL = "api/UpdateRecipientStatus/";
    const req_body = {
      Id: id,
      isActivated: isActivated,
    };

    const actionText = isActivated ? "activated" : "deactivated";

    api
      .post(API_URL, req_body, {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      })
      .then((response) => {
        setIsLoading(false);

        if (response && response.data !== undefined && response.data !== null) {
          const returnedStatus = response.data;

          if (typeof returnedStatus === "boolean") {
            setModalOpen(false);
            showSuccess(`Recipient ${actionText} successfully.`);
          } else if (returnedStatus.StatusCode === 1) {
            setModalOpen(false);
            showSuccess(
              returnedStatus.Message ||
                `Recipient ${actionText} successfully.`
            );
          } else {
            const errorMsg =
              returnedStatus.Message ||
              `Failed to ${actionText} recipient. Please try again.`;
            showError(errorMsg);
            setModalOpen(false);
          }
        } else {
          showError("Invalid response from server. Please try again.");
          setModalOpen(false);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        setModalOpen(false);
        console.error("Update recipient status error:", error);

        if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
          showError(
            "Unable to connect to server. Please check your internet connection."
          );
        } else if (error.response) {
          const errorMsg =
            error.response.data?.Message ||
            error.response.data?.message ||
            `Failed to ${actionText} recipient. Please try again.`;
          showError(errorMsg);
        } else {
          showError("An unexpected error occurred. Please try again.");
        }
      });
  };

  return (
    <>
      {isLoading && <Loader />}

      <div className="bg-white p-4 rounded-lg flex flex-col h-full min-h-0">
        <div className="mb-4">
          <h1 className="text-2xl font-semibold leading-[1.3]">Recipients</h1>
          <p className="text-base text-primary">Recipients</p>
        </div>

        <RecipientsToolbar setSearchText={setSearchText} />

        <div className="flex-1 min-h-0">
          <RecipientsTable
            data={items}
            onToggleStatus={handleToggleStatus}
            onEdit={handleEdit}
          />
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalRecords={items.length}
          onPageChange={(newPage) => {
            setCurrentPage(newPage);
          }}
        />

        <GenericModal
          open={modalOpen}
          title="Confirm Action"
          description={`Are you sure you want to ${
            pendingStatus === true ? "activate" : "deactivate"
          } this recipient?`}
          primaryText="Yes"
          secondaryText="No"
          loading={isLoading}
          onPrimary={() => {
            updateStatus(pendingId, pendingStatus);
          }}
          onClose={() => {
            setModalOpen(false);
            setPendingId(null);
            setPendingStatus(null);
          }}
        />

        <GenericModal
          open={modalState.open}
          title={modalState.title}
          description={modalState.description}
          type={modalState.type}
          primaryText="OK"
          onPrimary={() => {
            const wasSuccess = modalState.type === "success";
            setModalState((prev) => ({ ...prev, open: false }));
            if (wasSuccess) {
              getList();
            }
          }}
          onClose={() => {
            const wasSuccess = modalState.type === "success";
            setModalState((prev) => ({ ...prev, open: false }));
            if (wasSuccess) {
              getList();
            }
          }}
        />
      </div>
    </>
  );
};

export default Recipients;
